import React, { useState, useEffect } from 'react'
import { EventType, SchemaManager } from '../schemamanager/schemaManager'
import { IAnyObj, IComponent, ICompStyles, IContainerComponent, IDataComponent, IDefaultStyles, IPropStyles } from '../schemamanager/types'
import merge from 'lodash.merge';
import {Element} from './element'
import { IClasses } from '../schemamanager/twtypes';
import { ColSpans } from '../schemamanager/columns';
import { IThemeColor, ThemeColors } from '../schemamanager/themeColors';

export interface ItemProps {
  sm: SchemaManager,
  comp: IComponent,
}

export interface IDataProps {
  value: () => any,
  updateValue: (value: any) => void,
  validate: (value: any) => any,
  error: () => string,
}

const events = [
  'onClick',
  'onDoubleClick',
  'onMouseDown',
  'onMouseEnter',
  'onMouseLeave',
  'onMouseMove',
  'onKeyDown',
  'onKeyPress',
  'onKeyUp',
  'onFocus',
  'onBlur',
  'onSelect',
  'onScroll',
  'onWheel'
]

const resolveClassNamesFromClasses = (classes?: IClasses): string => {
  let ret = ''
  if (classes) {
    Object.values(classes).forEach((v: any) => {
      if (typeof v === 'object') {
        Object.values(v).forEach(nv => ret = ret + ' ' + nv)
      } else {
        ret = ret + ' ' + v
      }
    })
  }
  return ret.trim()
}

const getLayoutStyle =  (comp: IContainerComponent, defaultStyles: IDefaultStyles)  => {
  const usesGrid = (): boolean => {
    let ind = -1
    if (comp.children) {
      ind = comp.children.findIndex(f => f.cols)
    }
    return ind > -1
  }

  comp.usesGrid = usesGrid()
  const ds = defaultStyles.styleChildren ? defaultStyles.styleChildren : defaultStyles.main
  // ds.classes!.padding = 'px-6 py-4'

  if (comp.usesGrid) {
    ds.classes!.display = 'grid'
    ds.classes!.gridTemplateColumns = 'grid-cols-12'
  }
}


const getColsClass = (comp: IComponent, defaultStyles: IDefaultStyles) => {
  const ds = defaultStyles.main
  const p = comp.parentComp as IContainerComponent
  if (p && p.usesGrid)
  {
    let hasSmallBreak = false
    if (comp.cols) {
      const p = comp.parentComp as IContainerComponent
      if (!p || !p.children) return
      if (!ds.classes) ds.classes = {}
      if (!ds.classes.responsive) ds.classes.responsive = {}
      const arr = comp.cols.split(' ')
      arr.forEach((c: string) => {
        if (c.indexOf('xs') === 0) {
          hasSmallBreak = true
          ds.classes!.gridColumnStartEnd = ColSpans[c]
        } else {
          ds.classes!.responsive!.gridColumnStartEnd = ds.classes!.responsive!.gridColumnStartEnd + ' ' + ColSpans[c]
        }
      });
    }
    if (!hasSmallBreak) ds.classes!.gridColumnStartEnd = ColSpans.xs12
  }

}

const getDiffStyle =  (sm: SchemaManager, comp: IComponent, defaultStyles: IDefaultStyles)  => {
  if (!sm.Diffsm) return
  if (sm.getDiffHighlight(comp)) {
    let style = [defaultStyles.main]

    if (comp.type === 'input' || comp.type === 'select') {
      style = [defaultStyles.main, defaultStyles.styleInput]
    } else if (comp.type === 'radiogroup') {
      style = [defaultStyles.main]
    } else if (comp.type === 'checkbox') {
      style = [defaultStyles.main, defaultStyles.styleLabel]
    }
    style.forEach(s => s.classes!.backgroundColor = sm.DiffClassName)
  }
}


const mergeStyles = (sm: SchemaManager, comp: IComponent) => (defaultStyles: IDefaultStyles): IPropStyles => {

  getLayoutStyle(comp as IContainerComponent, defaultStyles)
  getColsClass(comp, defaultStyles)
  getDiffStyle(sm, comp, defaultStyles)

  const compStyles: ICompStyles = {}

  Object.keys(defaultStyles).forEach(prop => {
    if (prop === 'main') {
      compStyles.main = {}
      if (comp.style) compStyles.main.style = comp.style
      if (comp.classes) compStyles.main.classes = comp.classes
      if (comp.className) compStyles.main.className = comp.className
    } else {
      if (comp[prop]) {
        compStyles[prop] = comp[prop]
      }
    }
  })
  const ret: IPropStyles = {}
  const mergedStyles = merge({}, defaultStyles, compStyles)
  Object.keys(mergedStyles).forEach(prop => {
    const classesClassName = resolveClassNamesFromClasses(mergedStyles[prop].classes)
    mergedStyles[prop].className = (classesClassName ? classesClassName + ' ' : '') + (mergedStyles[prop].class ? mergedStyles[prop].class + ' ' : '') + (mergedStyles[prop].className ? mergedStyles[prop].className : '')
    ret[prop] = {}
    if (mergedStyles[prop].className) ret[prop].className = mergedStyles[prop].className
    if (mergedStyles[prop].style) ret[prop].style = mergedStyles[prop].style
  })

  return ret


}

const getDataItemProps = (sm: SchemaManager, comp: IComponent): IDataProps => {
  const c = comp as IDataComponent
  if (c.field) {
    return {
      value() {
        return sm.getValue(comp)
      },
      updateValue(value: any) {
        return sm.updateValue(comp, value)
      },
      validate(value: any) {
        return sm.validate(c, value)
      },
      error() {
        return sm.getError(c)
      }
    }
  } else {
    return {
      value() {
        return undefined
      },
      updateValue(_: any) {

      },
      validate() {
        return ''
      },
      error() {
        return ''
      }
    }

  }
}

const getEventsItemProp = (sm: SchemaManager, comp: IComponent) => () => {
  const events_obj = {}
  events.forEach((e) => {
    if (comp[e]) events_obj[e] = (event: any) => comp[e]({ sm, comp, event })
  })
  return events_obj
}

const getAddProps = (sm: SchemaManager, comp: IComponent) => () => {

  let addProps: IAnyObj = comp.addProps ? comp.addProps : {}
  if (comp.tooltip) {
    addProps['data-tip'] = comp.tooltip
    const standardProps: IAnyObj = {
      ['data-type']:  'dark',
      ['data-effect']:  'float'
    }

    if (sm.Schema.tooltipProps) {
      addProps = {
        ...standardProps,
        ...sm.Schema.tooltipProps,
        ...addProps
      }
    }
  }

  const disabled = sm.IsCompDisabled(comp)
  if (disabled) {
    addProps.disabled = true
  } else {
    delete addProps.disabled
  }

  if (comp.type === 'button') {
    const type = comp.buttonType ? comp.buttonType : 'button'
    addProps.type = type
  } else if (comp.type === 'link') {
    if (!disabled) {
      addProps.href = comp['href']
      if (comp['openInNewTab']) addProps.target="_blank"
    }
  }
  return addProps

}


const getThemeColor = (sm: SchemaManager, comp: IComponent) => (): IThemeColor  => {
  const _getThemeColor = (c: IComponent): IThemeColor =>  {
    let tc = ThemeColors[c.themeColor!]
    if (!tc) {
      tc = ThemeColors.blue
    }
    return tc
  }

  if (comp.themeColor) {
    return _getThemeColor(comp)
  }  else {
    let p = comp.parentComp
    while (p) {
      if (p.themeColor) return _getThemeColor(p)
      p = p.parentComp
    }
    return sm.ThemeColors
  }
}

const validateOnBlur = (sm: SchemaManager, comp: IComponent) => ()  => {
  const c = comp as IDataComponent
  const oldError = sm.getError(c) || ''
  const value = sm.getValue(comp)
  const error = sm.validate(c, value) || ''

  if (error !== oldError) {
    if (comp.parentComp && comp.parentComp.type === 'field') {
      sm.RenderComponent(comp.parentComp)
    } else {
      sm.RenderComponent(comp)
    }
  }

  if (comp.onBlur) {
    comp.onBlur({ sm, comp })
  }

}


const getChildrenList: any = (props: ItemProps) => {
  const { sm, comp } = props
  const cmp = comp as IContainerComponent

  if (!cmp.children) {
    return null
  }

  return cmp.children
    .map(c => {
      const key = sm.getObjectId(c)
      return <Item key={key} sm={sm} comp={c}/>
    })
}

export const Item = (props: ItemProps) => {
  const { sm, comp } = props
  const [counter, setCounter] = useState(0)

  const doRender = (compToRender: IComponent) => {
    if (compToRender === comp) {
      setCounter((curCounter) => curCounter + 1)
    }
  }

  const openModal = (compToOpen: IComponent) => {
    if (compToOpen === comp) {
      comp.hidden = false
      setCounter((curCounter) => curCounter + 1)
    }
  }

  const closeModal = (compToClose: IComponent) => {
    if (compToClose === comp) {
      comp.hidden = true
      setCounter((curCounter) => curCounter + 1)
    }
  }


  useEffect(() => {
    sm.on(EventType.RenderComponent, doRender)
    if (comp.type === 'modal') {
      sm.on(EventType.OpenModal, openModal)
      sm.on(EventType.CloseModal, closeModal)
    }
    return () => {
      sm.removeListener(EventType.RenderComponent, doRender)
      if (comp.type === 'modal') {
        sm.removeListener(EventType.OpenModal, openModal)
        sm.removeListener(EventType.CloseModal, closeModal)
      }
    }
  }, [sm])

  if (sm.getPropValue(comp, 'hidden')) return null

  return (
    <Element key={counter} sm={sm} comp={comp} mergeStyles={mergeStyles(sm,comp)} themeColor={getThemeColor(sm, comp)} validateOnBlur={validateOnBlur(sm, comp)} events={getEventsItemProp(props.sm, props.comp)} data={getDataItemProps(props.sm, props.comp)} addProps={getAddProps(sm, comp)}>
      {getChildrenList(props)}
    </Element>
  )
}
